import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { agregarTarea } from "../../../features/registroTareasSlice";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Material UI y Lucide
import { 
  Box, Typography, TextField, Grid, Button, MenuItem, Paper 
} from "@mui/material";
import { PlusCircle, Palette, Calendar, Image as ImageIcon, Layout } from "lucide-react";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = { 
  ice: "#C2EDF0", 
  aqua: "#00C0CA", 
  teal: "#008B9C", 
  dark: "#22404D", 
  slate: "#94A3B8",
  pearl: "#EEF3F4"
};

const Disenio = ({ idPedido }) => {
    // ==========================================
    // LÓGICA ORIGINAL (MANTENIDA)
    // ==========================================
    const tareas = useSelector(state => state.tareas.Tareas);
    const dispatch = useDispatch();
    const [formDisenio, setFormDisenio] = useState({
        tipoTarea: "Disenio",
        descripcion: "",
        fechaLimite: "",
        logo: "",
        formato: "Digital"
    });
    const [visible, setVisible] = useState(true);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormDisenio({
            ...formDisenio,
            [name]: value
        });
    };

    const validar = () => {
        const { descripcion, fechaLimite, logo } = formDisenio;
        const fechaFormulario = new Date(fechaLimite);
        const fechaActual = new Date();
        fechaActual.setHours(0, 0, 0, 0);
        fechaFormulario.setHours(0, 0, 0, 0);

        if (!descripcion) return "La descripción no puede estar vacía.";
        if (!fechaLimite) return "La fecha límite no puede estar vacía.";
        if (fechaFormulario < fechaActual) return "La fecha límite debe ser una fecha futura.";
        if (!logo) return "El logo no puede estar vacío.";
        return ""; 
    };

    const agregarTareaAlPedido = async () => {
        const mensaje = validar();
        if (mensaje !== "") {
            toast.error(mensaje, { position: "bottom-center", autoClose: 3000 });
            return;
        }

        const { descripcion, fechaLimite, formato, logo } = formDisenio;
        const registro = {
            id: tareas.length + 1,
            tipoTarea: "Disenio",
            descripcion,
            fechaLimite,
            comentario:"",
            logo,
            formato,
            estado: "Pendiente"
        };

        if (idPedido !== 0) {   
            const respuesta = await fetch(`${apiUrl}/Tarea/AgregarTareaDisenio?IdPedido=${idPedido}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apiKey': localStorage.getItem("apiKey")                                       
                },
                body: JSON.stringify(registro)
            });

            if (respuesta.ok) {
                setVisible(false);
                toast.success('Pedido agregado correctamente', { position: "bottom-center", autoClose: 3000 });
            }
        } else {
            dispatch(agregarTarea(registro));
            setVisible(false);
            toast.success('Tarea agregada correctamente', { position: "bottom-center", autoClose: 3000 });
        }
    };
    // ==========================================

    if (!visible) return null;

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                p: 3, 
                borderRadius: '24px', 
                bgcolor: PALETTE.pearl, 
                border: `1px solid ${PALETTE.ice}`,
                mt: 2,
                animation: 'fadeIn 0.3s ease-in-out'
            }}
        >
            <Stack spacing={2.5}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Palette size={20} color={PALETTE.teal} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: PALETTE.dark }}>
                        Detalles de Diseño
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>DESCRIPCIÓN DE LA TAREA</Typography>
                        <TextField 
                            fullWidth 
                            name="descripcion" 
                            value={formDisenio.descripcion} 
                            onChange={handleChange} 
                            placeholder="Ej: Flyer para redes sociales"
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>FECHA LÍMITE</Typography>
                        <TextField 
                            fullWidth 
                            type="date" 
                            name="fechaLimite" 
                            value={formDisenio.fechaLimite} 
                            onChange={handleChange} 
                            inputProps={{ min: new Date().toISOString().split('T')[0] }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>FORMATO</Typography>
                        <TextField 
                            select 
                            fullWidth 
                            name="formato" 
                            value={formDisenio.formato} 
                            onChange={handleChange}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }}
                        >
                            <MenuItem value="Digital">Digital</MenuItem>
                            <MenuItem value="Impreso">Impreso</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>LOGO A UTILIZAR</Typography>
                        <TextField 
                            fullWidth 
                            name="logo" 
                            value={formDisenio.logo} 
                            onChange={handleChange} 
                            placeholder="Ej: Logo Institucional / Logo Programa"
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>
                </Grid>

                <Button 
                    variant="contained" 
                    fullWidth 
                    onClick={agregarTareaAlPedido}
                    startIcon={<PlusCircle size={18} />}
                    sx={{ 
                        mt: 1, 
                        borderRadius: '12px', 
                        py: 1.2, 
                        fontWeight: 800, 
                        bgcolor: PALETTE.teal,
                        '&:hover': { bgcolor: PALETTE.dark }
                    }}
                >
                    Añadir esta tarea
                </Button>
            </Stack>
        </Paper>
    );
};

// Pequeño stack para manejar el layout interno
const Stack = ({ children, spacing }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing }}>
        {children}
    </Box>
);

export default Disenio;